import { DateTime } from 'luxon'
import type { Schema } from '@/models/models.ts'
import { type APIDisorder, type Disorder, parseDisorder } from '@/models/disorder.ts'

type APIUser = {
  id: number
  name: string
  username: string
  email?: string
  created_at: string
  updated_at: string
}

/**
 * Represents a user that can log into the therapist portal.
 * So therapists, admins, etc. but NOT patients.
 */
type User = {
  id: number
  name: string
  username: string
  email?: string
  created_at: DateTime
  updated_at: DateTime
}

/**
 * The result of calling /api/v1/user which also includes our frontend config for convenience
 */
type APICurrentUser = {
  user: APIUser
  config: {
    disorders: Array<APIDisorder>
  }
}

type Config = {
  disorders: Array<Disorder>
}

type CurrentUser = {
  user: User
  config: Config
}

const parseUser = function (json: APIUser): User {
  return {
    id: json.id,
    name: json.name,
    username: json.username,
    email: json.email,
    created_at: DateTime.fromISO(json.created_at),
    updated_at: DateTime.fromISO(json.updated_at),
  }
}

const parseCurrentUser = function (json: APICurrentUser): CurrentUser {
  return {
    user: parseUser(json.user),
    config: {
      disorders: json.config.disorders.map((item) => {
        return parseDisorder(item)
      }),
    },
  }
}

const UserSchema: Schema<APIUser, User> = {
  apiType: {} as APIUser,
  modelType: {} as User,
  parser: parseUser,
}

const CurrentUserSchema: Schema<APICurrentUser, CurrentUser> = {
  apiType: {} as APICurrentUser,
  modelType: {} as CurrentUser,
  parser: parseCurrentUser,
}

export { parseUser, UserSchema, parseCurrentUser, CurrentUserSchema }
export type { APIUser, User, APICurrentUser, CurrentUser, Config }
